import type { CreditType } from "@repo/config";
import { useSession } from "@saas/auth/hooks/use-session";
import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// API返回的信用点信息类型
interface CreditInfo {
	remaining: number;
	isUnlimited: boolean;
}

// 查询键
export const creditsQueryKey = ["credits"];
export const creditTypeQueryKey = (type: CreditType) => [
	...creditsQueryKey,
	type,
];

// 信用额度Hook
export const useCredits = () => {
	const queryClient = useQueryClient();
	const { user } = useSession();
	const isAuthenticated = !!user;

	// 获取所有类型的信用额度
	const { data: credits, isLoading } = useQuery({
		queryKey: creditsQueryKey,
		queryFn: async () => {
			const response = await apiClient.credits.$get();

			if (!response.ok) {
				throw new Error("Failed to fetch credits");
			}

			return response.json().then((res: any) => res.data);
		},
		// Only fetch credits when user is authenticated
		enabled: isAuthenticated,
	});

	// 使用信用额度
	const useCreditMutation = useMutation({
		mutationFn: async ({
			type,
			amount = 1,
		}: { type: CreditType; amount?: number }) => {
			// Only attempt to use credits if user is authenticated
			if (!isAuthenticated) {
				throw new Error("User not authenticated");
			}

			// 使用直接的路径字符串和类型作为参数
			const response = await fetch(`/api/credits/${type}/use`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				body: JSON.stringify({ amount }),
			});

			if (!response.ok) {
				throw new Error("Failed to use credit");
			}

			return response.json().then((res: any) => res.data);
		},
		onSuccess: (data, variables) => {
			const { type } = variables;
			// 更新缓存
			queryClient.setQueryData(creditsQueryKey, (oldData: any) => {
				if (!oldData) return oldData;

				return {
					...oldData,
					[type]: {
						remaining: data.remaining,
						isUnlimited: oldData[type]?.isUnlimited || false,
					},
				};
			});
		},
	});

	// 检查是否有足够的信用额度
	const checkCreditsMutation = useMutation({
		mutationFn: async (type: CreditType) => {
			// Only check credits if user is authenticated
			if (!isAuthenticated) {
				return false;
			}

			// 使用直接的路径字符串和类型作为参数
			const response = await fetch(`/api/credits/${type}/check`, {
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("Failed to check credits");
			}

			return response.json().then((res: any) => res.data.hasCredits);
		},
	});

	// 获取特定类型的剩余信用额度
	const getRemainingCredits = (type: CreditType) => {
		// Return 0 if not authenticated
		if (!isAuthenticated) return 0;
		if (!credits || !(type in credits)) return 0;
		return credits[type]?.remaining || 0;
	};

	// 检查特定类型的信用点是否为无限
	const hasUnlimitedCredits = (type: CreditType) => {
		// Return false if not authenticated
		if (!isAuthenticated) return false;
		// 当credits为null时返回false（数据未加载）
		if (!credits || !(type in credits)) return false;

		// 直接返回API提供的isUnlimited字段
		return credits[type]?.isUnlimited || false;
	};

	// 使用信用额度
	const useCredit = async (type: CreditType, amount?: number) => {
		// Only proceed if user is authenticated
		if (!isAuthenticated) {
			throw new Error("User not authenticated");
		}
		return useCreditMutation.mutateAsync({ type, amount });
	};

	// 检查是否有足够的信用额度
	const hasEnoughCredits = async (type: CreditType) => {
		return checkCreditsMutation.mutateAsync(type);
	};

	return {
		credits,
		isLoading,
		getRemainingCredits,
		hasUnlimitedCredits,
		useCredit,
		hasEnoughCredits,
		isUsingCredit: useCreditMutation.isPending,
		isCheckingCredits: checkCreditsMutation.isPending,
	};
};
